/* eslint-disable react/display-name */
import { Button, Popover, Select, Space, Table, TableProps, Tag, Typography } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import styles from 'src/styles/layout.module.css';

import TextArea from 'antd/es/input/TextArea';
import {
  deleteCmd,
  deleteHeadsetCmd,
  fetchCmd,
  sendCmd,
  sendHeadsetCmd,
} from '../../../../../features/companyCmd/companyCmd.state';
import { useCompanyCmd, useCompanyCmdDevicesDict } from '../../../../../features/companyCmd/companyCmd.hooks';
import { AppDispatch } from '../../../../../index';
import { useDevicesByCompanyId } from 'src/features/devices/devices.hooks';
import { Device } from 'src/features/devices/devices.types';
import { CrownTwoTone } from '@ant-design/icons';
import CardStatus from 'src/components/atoms/CardsStatus';
import HeadsetWorkingMode from 'src/components/atoms/HeadsetWorkingMode';
import BatteryStatus from 'src/components/atoms/BatteryStatus/BatteryStatus';
import ComponentRoleProtected from 'src/components/blocks/ComponentRoleProtected';
import { RoleEnum } from 'src/features/users/users.types';
import ConfirmButton from 'src/components/atoms/ConfirmButton';

const CompanyCmdTab: React.FC = () => {
  const { companyId } = useParams() as {
    companyId: string;
  };
  const currentCmd = useCompanyCmd();
  const [devices, { fetchCompanyDevices }] = useDevicesByCompanyId({
    companyId,
  });
  const deviceWithCommand = useCompanyCmdDevicesDict();

  const [cmd, setCmd] = useState('{}');
  const [error, setError] = useState<string | undefined>(undefined);
  const dispatch: AppDispatch = useDispatch();

  useEffect(() => {
    setCmd(JSON.stringify(currentCmd.payload, null, 2));
  }, [currentCmd]);

  useEffect(() => {
    dispatch(fetchCmd({ companyId }));
    fetchCompanyDevices();
  }, [companyId, dispatch, fetchCompanyDevices]);

  const onSendClick = useCallback(
    async (cmd: string) => {
      let jsonCmd;
      try {
        jsonCmd = JSON.parse(cmd);
        setError(undefined);
      } catch (e) {
        setError(`can't parse to JSON: ${e}`);
        return;
      }
      await dispatch(
        sendCmd({
          companyId,
          data: {
            payload: jsonCmd,
          },
        })
      );
    },
    [companyId, dispatch]
  );
  const onDeleteClick = useCallback(async () => {
    await dispatch(deleteCmd({ companyId }));
  }, [companyId, dispatch]);

  const isCmdEmpty = useMemo(() => {
    try {
      return Object.keys(JSON.parse(cmd)).length === 0;
    } catch (e) {
      return true; // error in the above string (in this case, yes)!
    }
  }, [cmd]);

  const onSendHeadsetClick = useCallback(
    async (cmd: string, headsetId: string) => {
      console.log('!!!!');
      let jsonCmd;
      try {
        jsonCmd = JSON.parse(cmd);
        setError(undefined);
      } catch (e) {
        setError(`can't parse to JSON: ${e}`);
        return;
      }
      await dispatch(
        sendHeadsetCmd({
          companyId,
          headsetId,
          data: {
            payload: jsonCmd,
          },
        })
      );
    },
    [companyId, dispatch]
  );

  const onDeleteHeadsetCommand = useCallback(
    async (headsetId: string) => {
      console.log('!!!!');
      await dispatch(
        deleteHeadsetCmd({
          companyId,
          headsetId,
        })
      );
    },
    [companyId, dispatch]
  );

  const tableConfig = useMemo<TableProps<Device>>(() => {
    const result: TableProps<Device> = {
      dataSource: devices,
      rowKey: values => values.id,
      columns: [
        {
          title: 'Метка шлема',
          dataIndex: 'label',
          key: 'label',
          render: (label: string, headset) => (
            <Space direction={'horizontal'} style={{ justifyContent: 'space-between', width: '100%' }}>
              <Space direction={'vertical'} size={0}>
                <Popover content={`ID Шлема: ${headset.phyDeviceId}`}>
                  <div>
                    <span style={{ textTransform: 'uppercase' }}>{label || '-'}</span>{' '}
                    {headset.activeSubscriptions.length > 0 && <CrownTwoTone twoToneColor='orange' />}
                  </div>
                </Popover>
                <Typography.Text type={'secondary'} style={{ fontSize: '12px' }}>
                  {headset.status?.firmware || '-'}
                </Typography.Text>
              </Space>
              <Space direction={'horizontal'}>
                <CardStatus status={headset.status} onlyWarning style={{ marginTop: 8 }} />
                {headset.hasActiveSession && <Tag color='green'>Используется</Tag>}
                {headset.isTest && <Tag color='blue'>Тестовый</Tag>}
              </Space>
            </Space>
          ),
        },
        {
          title: 'Команда',
          dataIndex: 'label',
          key: 'label',
          render: (label: string, headset) => (
            <span>
              {deviceWithCommand[headset.phyDeviceId] &&
                JSON.stringify(deviceWithCommand[headset.phyDeviceId], null, 2)}
            </span>
          ),
        },
        {
          title: 'Действия',
          dataIndex: 'actions',
          key: 'actions',
          align: 'right',
          render: (value, device) => (
            <ComponentRoleProtected roles={[RoleEnum.admin]}>
              {deviceWithCommand[device.phyDeviceId] ? (
                <ConfirmButton danger onClick={() => onDeleteHeadsetCommand(device.id)}>
                  Удалить
                </ConfirmButton>
              ) : (
                <ConfirmButton
                  type={'primary'}
                  onClick={() => onSendHeadsetClick(cmd, device.id)}
                  disabled={isCmdEmpty}
                >
                  Отправить команду
                </ConfirmButton>
              )}
            </ComponentRoleProtected>
          ),
        },
      ],
    };
    return result;
  }, [devices]);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Space style={{ width: '100%', justifyContent: 'space-between', textAlign: 'center', marginBottom: '0.5em' }}>
          <Typography.Title level={4}>Команда</Typography.Title>
          <Space>
            <Button danger type={'primary'} onClick={() => onDeleteClick()}>
              Удалить комманду
            </Button>
            <Button type='primary' onClick={() => onSendClick(cmd)} disabled={isCmdEmpty}>
              Отправить команду
            </Button>
          </Space>
        </Space>
      </div>
      <div className={styles.content}>
        {error && <Typography.Text type='danger'>{error}</Typography.Text>}
        <TextArea autoSize value={cmd} onChange={e => setCmd(e.target.value)} />
      </div>
      <div>
        <Table {...tableConfig} size='middle' pagination={{ pageSize: 50 }} />
      </div>
    </div>
  );
};

export default CompanyCmdTab;
